import { useEffect, useLayoutEffect, useRef } from "react";

/** The image files of a paste or drop, in order. */
function imageFiles(data: DataTransfer | null): File[] {
  const files: File[] = [];
  for (const item of Array.from(data?.items ?? [])) {
    if (item.kind !== "file" || !item.type.startsWith("image/")) continue;
    const file = item.getAsFile();
    if (file) files.push(file);
  }
  return files;
}

/**
 * ⌘V with images in the clipboard, anywhere in the document while
 * `enabled`, hands them to `onImages`. Focus inside an input, textarea or
 * contenteditable bails, so the Doc editor's own paste handler runs.
 */
export function useDocumentImagePaste(
  enabled: boolean,
  onImages: (files: File[]) => void,
): void {
  const onImagesRef = useRef(onImages);
  useLayoutEffect(() => {
    onImagesRef.current = onImages;
  }, [onImages]);

  useEffect(() => {
    if (!enabled) return;
    function handlePaste(event: ClipboardEvent) {
      const active = document.activeElement as HTMLElement | null;
      if (
        active &&
        (active.tagName === "INPUT" ||
          active.tagName === "TEXTAREA" ||
          active.isContentEditable)
      ) {
        return;
      }
      const files = imageFiles(event.clipboardData);
      if (files.length === 0) return;
      event.preventDefault();
      onImagesRef.current(files);
    }
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [enabled]);
}
